import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import { trackPageView } from './utils/analytics';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import EmployeeChat from './pages/EmployeeChat';
import Brain from './pages/Brain';
import Tasks from './pages/Tasks';
import Integrations from './pages/Integrations';
import Billing from './pages/Billing';
import Settings from './pages/Settings';
import Team from './pages/Team';
import Analytics from './pages/Analytics';
import APIPage from './pages/APIPage';
import AcceptInvite from './pages/AcceptInvite';
import TrelloCallback from './pages/TrelloCallback';
import AuthCallback from './pages/AuthCallback';
import StudentPlan from './pages/StudentPlan';
import GiftCards from './pages/GiftCards';
import PresentationBuilder from './pages/PresentationBuilder';
import Onboarding from './pages/Onboarding';
import Inbox from './pages/Inbox';
import SocialMediaManager from './pages/SocialMediaManager';
import Communication from './pages/Communication';
import GoogleSuite from './pages/GoogleSuite';
import DailySummarizer from './pages/DailySummarizer';

// Static Pages - Product
import AIEmployees from './pages/static/AIEmployees';
import Features from './pages/static/Features';
import Pricing from './pages/static/Pricing';
import IntegrationsPage from './pages/static/IntegrationsPage';
import API from './pages/static/API';

// Static Pages - Company
import About from './pages/static/About';
import Blog from './pages/static/Blog';
import BlogPost from './pages/static/BlogPost';
import Careers from './pages/static/Careers';
import Press from './pages/static/Press';
import Partners from './pages/static/Partners';
import Contact from './pages/static/Contact';

// Static Pages - Resources
import Documentation from './pages/static/Documentation';
import GettingStarted from './pages/static/GettingStarted';
import BrainAIDocs from './pages/static/BrainAIDocs';
import IntegrationsDocs from './pages/static/IntegrationsDocs';
import APIReferenceDocs from './pages/static/APIReferenceDocs';
import AccountBillingDocs from './pages/static/AccountBillingDocs';
import PrivacySecurityDocs from './pages/static/PrivacySecurityDocs';
import TroubleshootingFAQDocs from './pages/static/TroubleshootingFAQDocs';
import AutomationsDocs from './pages/static/AutomationsDocs';
import HelperSkillsDocs from './pages/static/HelperSkillsDocs';
import HelpCenter from './pages/static/HelpCenter';
import LegacyProducts from './pages/static/LegacyProducts';

// Article Pages
import WhatIsSweetPilot from './pages/static/articles/WhatIsSweetPilot';
import PlansAndPricing from './pages/static/articles/PlansAndPricing';
import LoginToSweetPilot from './pages/static/articles/LoginToSweetPilot';
import GettingStartedArticle from './pages/static/articles/GettingStartedArticle';
import NavigateWebApp from './pages/static/articles/NavigateWebApp';
import UILanguages from './pages/static/articles/UILanguages';
import DemoFreeTrial from './pages/static/articles/DemoFreeTrial';
import AIModels from './pages/static/articles/AIModels';
import MultiLanguageSupport from './pages/static/articles/MultiLanguageSupport';
import AIHallucinations from './pages/static/articles/AIHallucinations';
import WhoBenefits from './pages/static/articles/WhoBenefits';
import SweetPilotLegacy from './pages/static/articles/SweetPilotLegacy';
import SweetPilotBots from './pages/static/articles/SweetPilotBots';
import SweetPilotPrompts from './pages/static/articles/SweetPilotPrompts';
import OnboardingTutorial from './pages/static/articles/OnboardingTutorial';
import BrainAITutorial from './pages/static/articles/BrainAITutorial';
import SocialMediaManagerTutorial from './pages/static/articles/SocialMediaManagerTutorial';
import AgentEmployee from './pages/static/employees/Agent';
import AriaEmployee from './pages/static/employees/Aria';
import NovaEmployee from './pages/static/employees/Nova';
import AtlasEmployee from './pages/static/employees/Atlas';
import SageEmployee from './pages/static/employees/Sage';
import EchoEmployee from './pages/static/employees/Echo';
import PulseEmployee from './pages/static/employees/Pulse';
import ScoutEmployee from './pages/static/employees/Scout';
import InkEmployee from './pages/static/employees/Ink';
import FluxEmployee from './pages/static/employees/Flux';
import SparkEmployee from './pages/static/employees/Spark';
import OrbitEmployee from './pages/static/employees/Orbit';
import SyncEmployee from './pages/static/employees/Sync';
import CodeEmployee from './pages/static/employees/Code';
import PixelEmployee from './pages/static/employees/Pixel';
import LegalEmployee from './pages/static/employees/Legal';
import MvpMakerEmployee from './pages/static/employees/MvpMaker';
import SlidesEmployee from './pages/static/employees/Slides';
import Community from './pages/static/Community';
import Templates from './pages/static/Templates';
import Status from './pages/static/Status';
import Roadmap from './pages/static/Roadmap';

// Static Pages - Legal
import Privacy from './pages/static/Privacy';
import Terms from './pages/static/Terms';
import Security from './pages/static/Security';
import Cookies from './pages/static/Cookies';
import GDPR from './pages/static/GDPR';
import RefundPolicy from './pages/static/RefundPolicy';

// Layouts
import DashboardLayout from './components/dashboard/DashboardLayout';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import PaymentSettings from './pages/admin/PaymentSettings';
import SiteSettings from './pages/admin/SiteSettings';
import BlogManagement from './pages/admin/BlogManagement';
import UserManagement from './pages/admin/UserManagement';
import RoadmapManagement from './pages/admin/RoadmapManagement';
import CouponManagement from './pages/admin/CouponManagement';

// Protected Route wrapper
function ProtectedRoute({ children, skipOnboarding = false }: { children: React.ReactNode; skipOnboarding?: boolean }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-purple"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to onboarding if not completed
  if (!skipOnboarding && user && !user.onboarding_completed && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

// Public Route (redirect to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-purple"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Check if there's a saved path from cross-tab logout (restore previous page)
    const savedPath = sessionStorage.getItem('sweetpilot-pre-logout-path');
    if (savedPath) {
      sessionStorage.removeItem('sweetpilot-pre-logout-path');
      return <Navigate to={savedPath} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { checkAuth } = useAuthStore();
  const location = useLocation();

  // Check authentication status on app mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/callback/trello" element={<TrelloCallback />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Onboarding (protected but skip onboarding check) */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute skipOnboarding>
            <Onboarding />
          </ProtectedRoute>
        }
      />

      {/* Static Pages - Product */}
      <Route path="/employees" element={<AIEmployees />} />
      <Route path="/employees/agent" element={<AgentEmployee />} />
      <Route path="/employees/aria" element={<AriaEmployee />} />
      <Route path="/employees/nova" element={<NovaEmployee />} />
      <Route path="/employees/atlas" element={<AtlasEmployee />} />
      <Route path="/employees/sage" element={<SageEmployee />} />
      <Route path="/employees/echo" element={<EchoEmployee />} />
      <Route path="/employees/pulse" element={<PulseEmployee />} />
      <Route path="/employees/scout" element={<ScoutEmployee />} />
      <Route path="/employees/ink" element={<InkEmployee />} />
      <Route path="/employees/flux" element={<FluxEmployee />} />
      <Route path="/employees/spark" element={<SparkEmployee />} />
      <Route path="/employees/orbit" element={<OrbitEmployee />} />
      <Route path="/employees/sync" element={<SyncEmployee />} />
      <Route path="/employees/code" element={<CodeEmployee />} />
      <Route path="/employees/pixel" element={<PixelEmployee />} />
      <Route path="/employees/legal" element={<LegalEmployee />} />
      <Route path="/employees/mvp" element={<MvpMakerEmployee />} />
      <Route path="/employees/slides" element={<SlidesEmployee />} />
      <Route path="/features" element={<Features />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/integrations" element={<IntegrationsPage />} />
      <Route path="/api" element={<API />} />

      {/* Static Pages - Company */}
      <Route path="/about" element={<About />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/press" element={<Press />} />
      <Route path="/partners" element={<Partners />} />
      <Route path="/contact" element={<Contact />} />

      {/* Static Pages - Resources */}
      <Route path="/docs" element={<Documentation />} />
      <Route path="/doc/collections/getting-started" element={<GettingStarted />} />
      <Route path="/doc/collections/brain-ai" element={<BrainAIDocs />} />
      <Route path="/doc/collections/integrations" element={<IntegrationsDocs />} />
      <Route path="/doc/collections/api-reference" element={<APIReferenceDocs />} />
      <Route path="/doc/collections/account-billing" element={<AccountBillingDocs />} />
      <Route path="/doc/collections/automations" element={<AutomationsDocs />} />
      <Route path="/doc/collections/helper-skills" element={<HelperSkillsDocs />} />
      <Route path="/doc/collections/privacy-security" element={<PrivacySecurityDocs />} />
      <Route path="/doc/collections/troubleshooting-faq" element={<TroubleshootingFAQDocs />} />
      <Route path="/doc/collections/legacy-products" element={<LegacyProducts />} />

      {/* Article Pages */}
      <Route path="/doc/article/what-is-sweetpilot" element={<WhatIsSweetPilot />} />
      <Route path="/doc/article/pricing-and-plan" element={<PlansAndPricing />} />
      <Route path="/doc/article/login-to-sweetpilot" element={<LoginToSweetPilot />} />
      <Route path="/doc/article/getting-started" element={<GettingStartedArticle />} />
      <Route path="/doc/article/navigate-web-app" element={<NavigateWebApp />} />
      <Route path="/doc/article/ui-languages" element={<UILanguages />} />
      <Route path="/doc/article/demo-free-trial" element={<DemoFreeTrial />} />
      <Route path="/doc/article/ai-models" element={<AIModels />} />
      <Route path="/doc/article/multi-language-support" element={<MultiLanguageSupport />} />
      <Route path="/doc/article/ai-hallucinations" element={<AIHallucinations />} />
      <Route path="/doc/article/who-benefits" element={<WhoBenefits />} />
      <Route path="/doc/article/sweetpilot-legacy" element={<SweetPilotLegacy />} />
      <Route path="/doc/article/sweetpilot-bots" element={<SweetPilotBots />} />
      <Route path="/doc/article/sweetpilot-prompts" element={<SweetPilotPrompts />} />
      <Route path="/doc/article/onboarding-tutorial" element={<OnboardingTutorial />} />
      <Route path="/doc/article/brain-ai-tutorial" element={<BrainAITutorial />} />
      <Route path="/doc/article/social-media-manager-tutorial" element={<SocialMediaManagerTutorial />} />

      <Route path="/help" element={<HelpCenter />} />
      <Route path="/community" element={<Community />} />
      <Route path="/templates" element={<Templates />} />
      <Route path="/status" element={<Status />} />
      <Route path="/roadmap" element={<Roadmap />} />

      {/* Static Pages - Legal */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/security" element={<Security />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/gdpr" element={<GDPR />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />

      {/* Protected Dashboard Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="inbox" element={<Navigate to="/dashboard" replace />} />
        <Route path="employees" element={<Employees />} />
        <Route path="employees/:employeeId" element={<EmployeeChat />} />
        <Route path="brain" element={<Brain />} />
        <Route path="automation" element={<Tasks />} />
        <Route path="social-media-manager" element={<SocialMediaManager />} />
        <Route path="communication" element={<Communication />} />
        <Route path="google-suite" element={<GoogleSuite />} />
        <Route path="daily-summarizer" element={<DailySummarizer />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="team" element={<Team />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="api" element={<APIPage />} />
        <Route path="billing" element={<Billing />} />
        <Route path="student-plan" element={<StudentPlan />} />
        <Route path="gift-cards" element={<GiftCards />} />
        <Route path="settings" element={<Settings />} />
        <Route path="presentations/:presentationId" element={<PresentationBuilder />} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="blog" element={<BlogManagement />} />
        <Route path="roadmap" element={<RoadmapManagement />} />
        <Route path="coupons" element={<CouponManagement />} />
        <Route path="payments" element={<PaymentSettings />} />
        <Route path="settings" element={<SiteSettings />} />
      </Route>

      {/* 404 */}
      <Route
        path="*"
        element={
          <div className="min-h-screen bg-dark-900 flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-6xl font-bold gradient-text mb-4">404</h1>
              <p className="text-text-secondary mb-8">Page not found</p>
              <a href="/" className="btn-primary">
                Go Home
              </a>
            </div>
          </div>
        }
      />
    </Routes>
  );
}
